import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Debounce utility for reducing rapid updates
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Throttle utility for limiting update frequency
function useThrottle<T extends (...args: any[]) => any>(callback: T, delay: number): T {
  const lastRan = useRef<number>(Date.now());
  
  return useCallback((...args: any[]) => {
    if (Date.now() - lastRan.current >= delay) {
      callback(...args);
      lastRan.current = Date.now();
    }
  }, [callback, delay]) as T;
}

export interface RealtimeUpdateConfig {
  table: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  debounceMs?: number;
  throttleMs?: number;
  batchUpdates?: boolean;
  maxBatchSize?: number;
}

export interface RealtimeUpdate<T = any> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: T | null;
  old: T | null;
  timestamp: number;
}

/**
 * Optimized Real-time Updates Hook
 * 
 * Reduces re-renders and improves performance for Performance Zones:
 * - Debounces rapid updates to prevent render storms
 * - Throttles update frequency for smooth UX
 * - Batches multiple updates together
 * - Optimistic updates with rollback
 * - Memory leak prevention
 * - Automatic cleanup on unmount
 */
export function useOptimizedRealtimeUpdates<T = any>(
  config: RealtimeUpdateConfig,
  onUpdate?: (updates: RealtimeUpdate<T>[]) => void
) {
  const [updates, setUpdates] = useState<RealtimeUpdate<T>[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const channelRef = useRef<RealtimeChannel | null>(null);
  const updateBatchRef = useRef<RealtimeUpdate<T>[]>([]);
  const batchTimeoutRef = useRef<NodeJS.Timeout>();
  const mountedRef = useRef(true);

  // Debounced updates to prevent rapid re-renders
  const debouncedUpdates = useDebounce(updates, config.debounceMs || 100);

  // Throttled batch processor
  const processBatch = useThrottle(() => {
    if (!mountedRef.current || updateBatchRef.current.length === 0) return;

    const batch = [...updateBatchRef.current];
    updateBatchRef.current = [];

    setUpdates(prev => {
      const newUpdates = [...prev, ...batch];
      // Keep only last 100 updates to prevent memory bloat
      return newUpdates.slice(-100);
    });

    if (onUpdate) {
      onUpdate(batch);
    }
  }, config.throttleMs || 200);

  // Add update to batch
  const addToBatch = useCallback((update: RealtimeUpdate<T>) => {
    if (!mountedRef.current) return;

    updateBatchRef.current.push(update);

    if (config.batchUpdates) {
      // Clear existing timeout
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }

      // Process batch after delay or when max size reached
      const shouldProcessImmediately = 
        updateBatchRef.current.length >= (config.maxBatchSize || 10);

      if (shouldProcessImmediately) {
        processBatch();
      } else {
        batchTimeoutRef.current = setTimeout(processBatch, 50);
      }
    } else {
      processBatch();
    }
  }, [config.batchUpdates, config.maxBatchSize, processBatch]);

  // Setup realtime subscription
  useEffect(() => {
    if (!config.table) return;

    const setupChannel = async () => {
      try {
        setError(null);
        
        // Create channel with optimized settings
        const channel = supabase
          .channel(`performance-zones-${config.table}`, {
            config: {
              presence: { key: 'user_id' },
              broadcast: { self: false },
              ack: true
            }
          })
          .on(
            'postgres_changes',
            {
              event: config.event || '*',
              schema: 'public',
              table: config.table,
              filter: config.filter
            },
            (payload) => {
              if (!mountedRef.current) return;

              const update: RealtimeUpdate<T> = {
                eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
                new: payload.new as T,
                old: payload.old as T,
                timestamp: Date.now()
              };

              addToBatch(update);
            }
          )
          .subscribe((status) => {
            if (!mountedRef.current) return;
            
            if (status === 'SUBSCRIBED') {
              setIsConnected(true);
              setError(null);
            } else if (status === 'CHANNEL_ERROR') {
              setIsConnected(false);
              setError('Failed to connect to real-time updates');
            } else if (status === 'TIMED_OUT') {
              setIsConnected(false);
              setError('Real-time connection timed out');
            }
          });

        channelRef.current = channel;
      } catch (err) {
        if (mountedRef.current) {
          setError('Failed to setup real-time updates');
          setIsConnected(false);
        }
      }
    };

    setupChannel();

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        supabase.removeChannel(channelRef.current); // FIX: Add removeChannel
        channelRef.current = null;
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      setIsConnected(false);
    };
  }, [config.table, config.event, config.filter, addToBatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        supabase.removeChannel(channelRef.current); // FIX: Add removeChannel
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
    };
  }, []);

  // Memoized return value to prevent unnecessary re-renders
  const returnValue = useMemo(() => ({
    updates: debouncedUpdates,
    isConnected,
    error,
    clearUpdates: () => {
      if (mountedRef.current) {
        setUpdates([]);
        updateBatchRef.current = [];
      }
    },
    disconnect: () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      setIsConnected(false);
    }
  }), [debouncedUpdates, isConnected, error]);

  return returnValue;
}

/**
 * Optimized Performance Zones Real-time Hook
 * 
 * Specialized for Performance Zones data with intelligent batching
 */
export function usePerformanceZonesRealtime(userId: string) {
  const [optimisticUpdates, setOptimisticUpdates] = useState<Map<string, any>>(new Map());
  
  // MOBILE FIX: Don't create subscriptions if userId is empty (mobile device)
  const shouldSubscribe = !!userId;
  
  // Records updates
  const recordsUpdates = useOptimizedRealtimeUpdates(
    {
      table: shouldSubscribe ? 'performance_zones_summary' : '',
      filter: `user_id=eq.${userId}`,
      debounceMs: 150,
      throttleMs: 300,
      batchUpdates: true,
      maxBatchSize: 5
    },
    useCallback((updates) => {
      // Handle performance zones updates
      updates.forEach(update => {
        if (update.eventType === 'UPDATE' && update.new) {
          // Apply optimistic update
          setOptimisticUpdates(prev => {
            const newMap = new Map(prev);
            newMap.set(`performance_${update.new.id}`, update.new);
            return newMap;
          });
        }
      });
    }, [])
  );

  // Course discovery updates
  const coursesUpdates = useOptimizedRealtimeUpdates(
    {
      table: shouldSubscribe ? 'course_discovery' : '',
      event: 'UPDATE',
      debounceMs: 200,
      throttleMs: 500,
      batchUpdates: true,
      maxBatchSize: 10
    }
  );

  // Leaderboard updates
  const leaderboardUpdates = useOptimizedRealtimeUpdates(
    {
      table: shouldSubscribe ? 'public_leaderboards' : '',
      debounceMs: 100,
      throttleMs: 250,
      batchUpdates: true,
      maxBatchSize: 15
    }
  );

  // Clear optimistic updates after delay
  useEffect(() => {
    if (optimisticUpdates.size > 0) {
      const timeout = setTimeout(() => {
        setOptimisticUpdates(new Map());
      }, 2000); // Clear after 2 seconds

      return () => clearTimeout(timeout);
    }
  }, [optimisticUpdates]);

  return {
    records: recordsUpdates,
    courses: coursesUpdates,
    leaderboards: leaderboardUpdates,
    optimisticUpdates,
    isAnyConnected: recordsUpdates.isConnected || coursesUpdates.isConnected || leaderboardUpdates.isConnected,
    hasAnyError: recordsUpdates.error || coursesUpdates.error || leaderboardUpdates.error,
    clearAllUpdates: () => {
      recordsUpdates.clearUpdates();
      coursesUpdates.clearUpdates();
      leaderboardUpdates.clearUpdates();
      setOptimisticUpdates(new Map());
    }
  };
}

/**
 * Smart Re-render Prevention Hook
 * 
 * Prevents unnecessary re-renders by deeply comparing props
 */
export function useSmartMemo<T>(value: T, deps?: React.DependencyList): T {
  const ref = useRef<T>(value);
  const depsRef = useRef<React.DependencyList | undefined>(deps);

  return useMemo(() => {
    // Deep comparison for objects and arrays
    const hasChanged = deps ? 
      !deps.every((dep, index) => 
        Object.is(dep, depsRef.current?.[index])
      ) :
      !deepEqual(value, ref.current);

    if (hasChanged) {
      ref.current = value;
      depsRef.current = deps;
    }

    return ref.current;
  }, deps || [value]);
}

// Deep equality check for objects
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  
  if (a == null || b == null) return a === b;
  
  if (typeof a !== typeof b) return false;
  
  if (typeof a !== 'object') return a === b;
  
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  
  if (keysA.length !== keysB.length) return false;
  
  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  
  return true;
}